import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from '@/app/layouts/Sidebar';
import { Topbar } from '@/app/layouts/Topbar';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { NAV_ITEMS_BY_PATH } from '@/app/navigation';
import { fetchAnalytics } from '@/features/dashboard/api';
import { queryKeys } from '@/lib/queryClient';

// -----------------------------------------------------------------------------
// The authenticated shell.
//
// Sidebar + top bar + a scrolling content column. Only the content column
// scrolls - the nav and the header stay put, which is what makes a long
// moderation queue usable.
//
// RESPONSIVE BEHAVIOUR. The rail is fixed at 260px from `lg` up and becomes an
// off-canvas drawer below it. That break is at 1024px rather than the more usual
// 768px because of what this panel actually contains: at 900px a 260px rail
// leaves 640px for a six-column table, which is not a narrow table, it is an
// unusable one. The drawer is the same `Sidebar` component, not a reduced mobile
// copy - see the note there.
//
// The content column is `min-w-0`. Without it a wide table inside a flex child
// sets the child's min-content width to the table's width, the shell grows past
// the viewport, and the whole app scrolls sideways behind a fixed sidebar. That
// single class is the difference between "responsive" and "looks fine until
// someone opens Payments".
// -----------------------------------------------------------------------------

export function AppLayout(): JSX.Element {
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // The nav badge counts ride on the dashboard analytics query, so opening the
  // panel does not fire a second request just to number the sidebar. A failure
  // here degrades to "no badge" rather than an error - the counts are a hint.
  const { data } = useQuery({
    queryKey: queryKeys.analytics,
    queryFn: ({ signal }) => fetchAnalytics(signal),
    staleTime: 60_000,
  });

  // Each badge reads its own dedicated count - see the note on the backend
  // queries in `admin.analytics.service.ts::getAnalytics`. Previously this
  // wired the COMBINED "everything pending" total to the Coach Applications
  // badge alone, so a pending certificate (or transformation, or media item)
  // showed up as a phantom application.
  const counts = {
    pendingApplications: data?.needsAttention.pendingApplicationCount ?? 0,
    reviewQueue: data?.needsAttention.reviewQueuePendingCount ?? 0,
    openTickets: data?.needsAttention.openTicketCount ?? 0,
  };
  const totalNeedsAttention = counts.pendingApplications + counts.reviewQueue + counts.openTickets;

  // Document title tracks the route, so browser history and pinned tabs are
  // legible when several screens are open at once.
  useEffect(() => {
    const label = NAV_ITEMS_BY_PATH[pathname]?.label;
    document.title = label ? `${label} · SECT.FIT Admin` : 'SECT.FIT Admin';
  }, [pathname]);

  // A route change closes the drawer. `Sidebar` also calls `onNavigate` on click,
  // which covers the common case; this covers the rest - a redirect, a Back, a
  // link followed from inside the content column while the drawer is open.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-canvas">
      {/* Desktop rail. `hidden` rather than a width transition: an animating
          260px column reflows every table in the content area on each frame. */}
      <div className="hidden w-sidebar shrink-0 lg:block">
        <Sidebar counts={counts} />
      </div>

      {/* Mobile drawer. Same component, same counts. */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent
          side="left"
          hideClose
          // `sm:max-w-[260px]` overrides the primitive's `sm:max-w-sm` (384px):
          // the drawer is the rail, and the rail is 260px at every width.
          className="w-sidebar max-w-[85vw] border-r-sidebar-border p-0 sm:max-w-[260px]"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar counts={counts} onNavigate={() => setNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar pendingCount={totalNeedsAttention} onOpenNav={() => setNavOpen(true)} />

        {/* The ONE scroll container in the app. `overscroll-contain` keeps a
            fling that reaches the last row from chaining out to the document -
            see the lock in `index.css`. */}
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain">
          {/* `max-w` caps the line length of a wide table on a 32" monitor;
              the bottom padding leaves the last row clear of the viewport edge,
              which is what stops a long list feeling like it was cut off.

              Gutters step 12 → 16 → 24 → 28px. On a phone a 28px gutter costs
              15% of the screen for nothing. */}
          <div className="mx-auto flex min-w-0 max-w-[1600px] flex-col gap-4 px-3 pb-12 pt-3 sm:px-4 sm:pb-14 sm:pt-4 lg:gap-5 lg:px-6 lg:pt-5 xl:px-7">
            {/* Every route is lazily loaded, so the shell needs a fallback that
                does not flash the layout apart while a chunk downloads. */}
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * The chunk-download fallback.
 *
 * Deliberately shaped like the screens it stands in for - a strip of tiles over
 * a bordered block - rather than a centred spinner. A spinner tells you to wait;
 * a shape tells you what is arriving, and the layout does not jump when it does.
 */
function RouteFallback(): JSX.Element {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading page">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-border bg-card p-3 shadow-card">
            <div className="flex items-center gap-2">
              <Skeleton className="size-7 rounded" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="mt-3 h-6 w-20" />
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card shadow-card">
        <div className="flex h-strip items-center gap-2 border-b border-border bg-canvas px-2.5">
          <Skeleton className="size-6 rounded" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex h-row items-center gap-4 px-3">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-3 max-w-[140px] flex-1" />
              <Skeleton className="hidden h-3 max-w-[140px] flex-1 sm:block" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
